import {
    BASIC_COMPOSITION_TYPE,
    ETK_CESAR,
    FREE_AMOUNT_ON_ORDER,
    FREE_AMOUNT_ON_PRODUCT,
    FREE_DELIVRY,
    FREE_PRODUCT,
    GODIA_SALES_MODE,
    PERCENT_ON_ORDER,
    PERCENT_ON_PRODUCT,
} from "@constants";
import { t } from "i18next";
import { produce } from "immer";
import { v4 as uuid } from "uuid";
import { snapshot } from "valtio";

import { ErrorLoyaltyToast, LoyaltyToast } from "@components/common/Toast";

import { kioskStore } from "@pages/Kiosk/store";

export function getLoyaltyTotal(orderItems) {
    let sum = 0;

    let order = JSON.parse(JSON.stringify(orderItems));

    order.forEach((item) => {
        let localShopCartSum = item?.fidelity ? item?.fidelity : 0;

        if (Object.keys(item?.shopCart).length > 0) {
            Object.values(item.shopCart).forEach((el) => {
                if (el.type !== BASIC_COMPOSITION_TYPE) {
                    localShopCartSum += getSum(el.compositions);
                }
            });
        }
        sum += localShopCartSum * item.quantity;
    });

    return sum;
}

function getSum(compositions) {
    let localSum = 0;

    Object.values(compositions).forEach((product) => {
        let localProductSum = product.fidelity ? product?.fidelity : 0;
        if (
            product.compositions !== undefined &&
            Object.keys(product.compositions).length > 0
        ) {
            localProductSum = localProductSum + getSum(product.compositions);
        }
        localSum += localProductSum;
    });

    return localSum;
}

export function getAvailablePromoCode(promos) {
    const { selectedSaleModeId } = snapshot(kioskStore);

    const userGiftsForCurrentSaleMode = selectedSaleModeId
        ? promos.filter((codePromo) =>
              codePromo.modeVente.includes(GODIA_SALES_MODE[selectedSaleModeId])
          )
        : promos;
    const result = userGiftsForCurrentSaleMode.reduce(
        (acc, promo) => {
            acc.minOrder.push(promo.minOrder);
            acc.maxOrder = Math.max(acc.maxOrder, promo.maxOrder);
            return acc;
        },
        { minOrder: [], maxOrder: 0 }
    );

    result.minOrder.sort((a, b) => a - b);
    return result;
}

export function calculateTotalOrderWithGift(
    initialPrice,
    gift,
    cardType,
    orderItems
) {
    return produce(
        { orderItems, totalPriceWithPromo: initialPrice },
        (draft) => {
            const discountTypeNum = parseFloat(gift.discountType);
            const discountValue = Math.max(0, gift.discountValue);

            switch (discountTypeNum) {
                case PERCENT_ON_ORDER:
                    draft.totalPriceWithPromo =
                        initialPrice * (1 - discountValue / 100);
                    break;

                case FREE_AMOUNT_ON_ORDER:
                    draft.totalPriceWithPromo =
                        cardType === ETK_CESAR
                            ? initialPrice * (1 - discountValue / initialPrice)
                            : initialPrice - discountValue;
                    break;

                case FREE_AMOUNT_ON_PRODUCT:
                case PERCENT_ON_PRODUCT:
                case FREE_PRODUCT: {
                    const eligibleProductIds = gift?.productsIds
                        ? gift.productsIds.split(",")
                        : [];

                    if (eligibleProductIds.length > 0) {
                        const eligibleItemIndex = draft.orderItems.findIndex(
                            (item) =>
                                eligibleProductIds.includes(item.reference) &&
                                !item.discountedPrice &&
                                !item.isReward
                        );

                        if (eligibleItemIndex !== -1) {
                            draft.orderItems[eligibleItemIndex].isPromo = true;
                            if (
                                draft.orderItems[eligibleItemIndex].quantity > 1
                            ) {
                                const discountedItem = {
                                    ...draft.orderItems[eligibleItemIndex],

                                    quantity: 1,
                                    iuudOrder: uuid(),
                                    parentOrderId:
                                        draft.orderItems[eligibleItemIndex]
                                            .iuudOrder,
                                };

                                applyProductDiscount(
                                    discountedItem,
                                    discountTypeNum,
                                    discountValue
                                );

                                draft.orderItems[
                                    eligibleItemIndex
                                ].quantity -= 1;

                                draft.orderItems.push(discountedItem);

                                draft.totalPriceWithPromo -=
                                    discountedItem.discountedPrice;
                            } else {
                                const eligibleItem =
                                    draft.orderItems[eligibleItemIndex];

                                applyProductDiscount(
                                    eligibleItem,
                                    discountTypeNum,
                                    discountValue
                                );

                                draft.totalPriceWithPromo -=
                                    eligibleItem.discountedPrice;
                            }
                        }
                    }
                    break;
                }

                case FREE_DELIVRY:
                    break;

                default:
                    break;
            }

            draft.totalPriceWithPromo = calculateTotalPriceWithPromo(
                Math.max(0, draft.totalPriceWithPromo)
            );
        }
    );
}

/**
 * Toggles a reward item selection.
 *
 * A product can be listed in several reward categories, so the item is matched
 * on its category as well: without `rewardCategoryId` the same product would be
 * selected in every category listing it.
 */
export function handleRewardSelection(
    rewards,
    iuud,
    menuItem = {},
    isModification,
    rewardCategoryId
) {
    return rewards.map((reward) => {
        const isTargetedCategory =
            rewardCategoryId === undefined ||
            reward.reward_id === rewardCategoryId;

        if (!isTargetedCategory) {
            return reward;
        }

        const newRewardList = reward.rewardList.map((rewardItem) => {
            if (rewardItem.iuud === iuud) {
                return isModification
                    ? { ...rewardItem, ...menuItem }
                    : {
                          ...rewardItem,
                          ...menuItem,
                          isSelected: !rewardItem.isSelected,
                      };
            }
            return rewardItem;
        });
        return {
            ...reward,
            rewardList: newRewardList,
        };
    });
}

/**
 * Builds the rewards part of the order payload.
 *
 * A product can be listed in several reward categories, so matching on the
 * ordered references alone would push one entry per listing category and charge
 * the loyalty points of each of them. Only the category the customer actually
 * took the product from is selected, hence the `isSelected` guard.
 */
export function getOrderRewardsPayload(rewards, orderedRewardReferences) {
    const finalRewardList = [];
    let loyaltyPayment = 0;

    rewards.forEach((rewardCategory) => {
        rewardCategory.rewardList
            .filter(
                (rewardItem) =>
                    rewardItem.isSelected &&
                    orderedRewardReferences.includes(rewardItem.reference)
            )
            .forEach((rewardItem) => {
                loyaltyPayment =
                    loyaltyPayment + Number(rewardCategory.loyalty_points);
                finalRewardList.push({
                    program_id: rewardCategory.program_id,
                    reward_id: rewardCategory.reward_id,
                    reward_name: rewardCategory.reward_name,
                    reward_item_reference: rewardItem.reference,
                    loyalty_points: rewardCategory.loyalty_points,
                });
            });
    });

    return { finalRewardList, loyaltyPayment };
}

/**
 * A product listed in several reward categories can only be taken once, so it is
 * disabled everywhere else as soon as it is selected in one category.
 */
export function isRewardSelectedInAnotherCategory(
    rewards,
    iuud,
    rewardCategoryId
) {
    return rewards.some(
        (reward) =>
            reward.reward_id !== rewardCategoryId &&
            reward.rewardList.some(
                (rewardItem) => rewardItem.iuud === iuud && rewardItem.isSelected
            )
    );
}

/**
 * A reward category lets the customer choose `qty` products, no more. A missing
 * or zero quantity means the category is not limited.
 */
export function isRewardCategoryQuotaReached(rewardList, qty) {
    const maxSelectionCount = Number(qty);

    if (!(maxSelectionCount > 0)) {
        return false;
    }

    const selectionCount = rewardList.filter(
        (rewardItem) => rewardItem.isSelected
    ).length;

    return selectionCount >= maxSelectionCount;
}

export function handleClearRewardSelection(rewards) {
    return rewards.map((reward) => {
        const newRewardList = reward.rewardList.map((rewardItem) => {
            return {
                ...rewardItem,
                isSelected: false,
            };
        });
        return {
            ...reward,
            rewardList: newRewardList,
        };
    });
}
export function getDiscountRate(
    totalOrder,
    discountType,
    discountValue,
    promoProduct
) {
    let discountRate = 0;
    let isOrderPromo = false;
    switch (parseFloat(discountType)) {
        case PERCENT_ON_ORDER:
            isOrderPromo = true;
            discountRate = discountValue;
            break;
        case FREE_AMOUNT_ON_ORDER:
            isOrderPromo = true;
            discountRate = convertAmountReductionToPercentage(
                discountValue,
                totalOrder
            );

            break;
        case PERCENT_ON_PRODUCT:
            discountRate = discountValue;
            break;
        case FREE_AMOUNT_ON_PRODUCT:
            discountRate = convertAmountReductionToPercentage(
                promoProduct.discountedPrice,
                promoProduct.initialPrice
            );
            break;
        case FREE_PRODUCT:
            discountRate = 100;
            break;
        case FREE_DELIVRY:
            break;
        default:
            break;
    }
    return { discountRate, isOrderPromo };
}
export function checkPromoCodeValidity(usedGifts, userGifts, totalOrder) {
    const { customerLanguage } = snapshot(kioskStore);
    if (usedGifts.length > 0) {
        let isPromoRemoved = false;

        usedGifts.forEach((gift) => {
            const giftName =
                gift.name[customerLanguage.name.toUpperCase()] !== ""
                    ? gift.name[customerLanguage.name.toUpperCase()]
                    : gift.name.default;
            if (gift.maxOrder > 0 && totalOrder > gift.maxOrder) {
                ErrorLoyaltyToast(
                    `${t(
                        `The reward {{giftName}} has been removed due to not meeting the maximum requirement`,
                        { giftName }
                    )}`
                );
                isPromoRemoved = true;
            }
            if (totalOrder < gift.minOrder) {
                ErrorLoyaltyToast(
                    `${t(
                        `The reward {{giftName}} has been removed due to not meeting the minimum requirement`,
                        {
                            giftName,
                        }
                    )}`
                );
                isPromoRemoved = true;
            }
        });
        if (isPromoRemoved) {
            usedGifts = usedGifts.filter(
                (gift) =>
                    totalOrder <= gift.maxOrder && totalOrder >= gift.minOrder
            );

            userGifts = userGifts.map((item) => {
                return {
                    ...item,
                    isSelected: false,
                };
            });
        }
        return { usedGifts, userGifts };
    }
}

export function unlockedPromoCode(state, totalPriceWithOutFidelity) {
    for (
        let i = state.promoCodesMinMaxOrder.minOrder.length - 1;
        state.promoCodesMinMaxOrder.minOrder[i] > state.promoCode;
        i--
    ) {
        if (
            totalPriceWithOutFidelity >=
                state.promoCodesMinMaxOrder.minOrder[i] &&
            totalPriceWithOutFidelity < state.promoCodesMinMaxOrder.maxOrder
        ) {
            if (state.promoCodesMinMaxOrder.minOrder[i] !== null) {
                state.promoCode = state.promoCodesMinMaxOrder.minOrder[i];
            }
            LoyaltyToast(
                `${t(`You have unlocked one or more promotions:`)} ${t(
                    `Click here to use it`
                )}`
            );
            break;
        }
    }
}

export function applyAllGiftsToOrder(
    usedGifts,
    orderItems,
    itemsPrice,
    cardType
) {
    if (orderItems.length === 0) {
        return {
            finalOrderItems: orderItems,
            finalTotalPrice: itemsPrice,
        };
    }
    let currentOrderItems = orderItems;
    let currentTotalPrice = itemsPrice;

    for (const gift of usedGifts) {
        const result = calculateTotalOrderWithGift(
            currentTotalPrice,
            gift,
            cardType,
            currentOrderItems
        );

        currentOrderItems = result.orderItems;
        currentTotalPrice = result.totalPriceWithPromo;
    }

    return {
        finalOrderItems: currentOrderItems,
        finalTotalPrice: currentTotalPrice,
    };
}

function calculateTotalPriceWithPromo(value) {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue) || numericValue <= 0) return 0;
    return Math.round(numericValue * 100) / 100;
}

export function resetOrderItems(orderItems, itemsPrice) {
    const isPromoActive = orderItems.some((order) => order.discountedPrice);

    if (!isPromoActive) {
        return { currentOrderItems: orderItems, currentTotalPrice: itemsPrice };
    }

    const updatedOrder = produce(orderItems, (draft) => {
        const promoItemIndex = draft.findIndex(
            (order) => order.discountedPrice
        );

        if (promoItemIndex !== -1) {
            itemsPrice += draft[promoItemIndex].discountedPrice;
            const parentItemIndex = draft.findIndex(
                (order) =>
                    order.iuudOrder === draft[promoItemIndex].parentOrderId
            );

            if (
                parentItemIndex !== -1 &&
                draft[promoItemIndex].comment === draft[parentItemIndex].comment
            ) {
                draft[parentItemIndex].quantity += 1;
                draft.splice(promoItemIndex, 1);
            } else {
                draft[promoItemIndex].isPromo = false;
                draft[promoItemIndex].price +=
                    draft[promoItemIndex].discountedPrice;
                delete draft[promoItemIndex].discountedPrice;
            }
        }
    });

    return {
        currentOrderItems: updatedOrder,
        currentTotalPrice: itemsPrice,
    };
}

function applyProductDiscount(eligibleItem, discountTypeNum, discountValue) {
    let itemDiscount = 0;

    if (discountTypeNum === FREE_AMOUNT_ON_PRODUCT) {
        itemDiscount = Math.min(discountValue, eligibleItem.initialPrice);
    } else if (discountTypeNum === PERCENT_ON_PRODUCT) {
        itemDiscount = eligibleItem.initialPrice * (discountValue / 100);
    } else if (discountTypeNum === FREE_PRODUCT) {
        itemDiscount = eligibleItem.initialPrice;
    }

    eligibleItem.discountedPrice = itemDiscount;
    eligibleItem.price -= itemDiscount;

    return eligibleItem;
}

function convertAmountReductionToPercentage(discountValue, totalOrder) {
    if (discountValue >= totalOrder) {
        return 100;
    } else {
        return (discountValue / totalOrder) * 100;
    }
}
